<script setup>
import { useForm } from '@inertiajs/vue3';
import { computed, ref, getCurrentInstance, toRaw, watch, reactive, onMounted } from 'vue';
import axios from 'axios';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Select from 'primevue/select';
import AutoComplete from 'primevue/autocomplete';
import InputText from 'primevue/inputtext';
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import Divider from 'primevue/divider';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import FloatLabel from 'primevue/floatlabel';
import InputNumber from 'primevue/inputnumber';
import Calendar from 'primevue/calendar';
import Checkbox from 'primevue/checkbox';
import Tag from 'primevue/tag';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import ContentModal from '@/components/ContentModal.vue';
import SiteFeaturesInfo from '@/components/SiteFeaturesInfo.vue';
import SendForApprovalModal from '@/components/admin/orders/SendForApprovalModal.vue';
import QuickOfferModal from '@/components/QuickOfferModal.vue';
import BudgetSuggestionsPanel from '@/views/linkarme/Admin/Budgets/BudgetSuggestionsPanel.vue';
import { useLoading } from '@/composables/useLoading';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const editingCurrency = ref('USD');

const showContentModal = ref(false);
const selectedItems = ref([]);
const siteFeatures = ref(null);
const selectedOrderItem = ref(null);
const submittingContent = ref(false);
const currentItemIndex = ref(null);
const itemContents = reactive({});
const expandedContentItems = ref({});
const showSendApprovalModal = ref(false);
const pendingApprovalIndex = ref(null);
const autoApproveContent = ref(false);

defineOptions({
    layout: AdminMainLayout,
});

const { formatWithSymbol, formatConvertedPrice, getCurrentCurrency, onCurrencyChange, exchangeRates, convertPrice } = useCurrencyFormatter();
const currentCurrency = ref(getCurrentCurrency());

const formattedLanguages = computed(() => {
    if (!languages) return [];

    return languages
        .map(lang => ({
            id: lang.id,
            name: lang.name,
            code: lang.code
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
});

const { coins, statuses, clients, sites, types, payment_settings, languages, sellers, editBudget, bestSellers, recommended } = defineProps({
    coins: Object,
    statuses: Object,
    clients: Array,
    sites: Object,
    types: Array,
    payment_settings: Object,
    languages: Array,
    sellers: Array,
    editBudget: Object,
    bestSellers: { type: Array, default: () => [] },
    recommended: { type: Array, default: () => [] },
});

const formatDate = (date) => {
    if(!date) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const form = useForm({
    client_id: null,
    payment_date: null,
    deferred_payment: false,
    notify_seller: false,
    notify_client: false,
    sites: [],
});



// Local storage key for draft order
const DRAFT_ORDER_KEY = 'draft_order_admin';

const loadDraftOrder = () => {
    // Auto-save disabled
    return;
    /*
    try {
        const savedDraft = localStorage.getItem(DRAFT_ORDER_KEY);
        if (savedDraft) {
            const draft = JSON.parse(savedDraft);

            if (draft.form) {
                Object.assign(form, draft.form);
                if (draft.form.receipt_date) {
                    form.receipt_date = new Date(draft.form.receipt_date);
                }
                if (draft.form.delivery_date) {
                    form.delivery_date = new Date(draft.form.delivery_date);
                }
                if (draft.form.payment_date) {
                    form.payment_date = new Date(draft.form.payment_date);
                }
            }

            if (draft.itemContents) {
                Object.assign(itemContents, draft.itemContents);
            }

            if (draft.publicationDates) {

                window.restoredPublicationDates = draft.publicationDates;
            }

            toast.add({
                severity: 'info',
                summary: $t('Draft Loaded'),
                detail: $t('Previous draft order has been restored'),
                life: 3000
            });
        }
    } catch (error) {
        console.error('Error loading draft order:', error);
        localStorage.removeItem(DRAFT_ORDER_KEY);
    }
};

const saveDraftOrder = () => {
    // Auto-save disabled
    return;
    /*
    try {
        const publicationDates = {};
        itemsWithContent.value.forEach(item => {
            if (item.content && item.content.publication_date) {
                publicationDates[item.content.id] = item.content.publication_date;
            }
        });

        const draft = {
            form: { ...form },
            itemContents: { ...itemContents },
            publicationDates: publicationDates,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem(DRAFT_ORDER_KEY, JSON.stringify(draft));
    } catch (error) {
        console.error('Error saving draft order:', error);
    }
    */
};

const clearDraftOrder = () => {
    localStorage.removeItem(DRAFT_ORDER_KEY);
};

onMounted(() => {
    if (editBudget) {
        form.client_id = editBudget.client_id;

        if (editBudget.items && editBudget.items.length > 0) {
            const processedSites = editBudget.processedSites || {};
            form.sites = editBudget.items.map(item => {
                const siteUrl = item.site?.url;
                const processedSite = siteUrl ? processedSites[siteUrl] : null;

                return {
                    id: item.site?.id,
                    url: item.site?.url || '-',
                    da: item.site?.da || 0,
                    dr: item.site?.dr || 0,
                    link_type: processedSite?.link_type || item.site?.link_type || 'DOFOLLOW',
                    type_id: item.type_id,
                    seller_id: item.seller_id,
                    seller: item.seller,
                    cost: (() => { const t = processedSite?.types?.find(x => x.id === item.type_id); return t?.cost ?? item.cost ?? 0; })(),
                    cost_coin: (() => { const t = processedSite?.types?.find(x => x.id === item.type_id); return t?.cost_coin || item.cost_coin || 'BRL'; })(),
                    sale: (() => { const t = processedSite?.types?.find(x => x.id === item.type_id); return t?.converted_price ?? item.sale ?? 0; })(),
                    sale_coin: (() => { const t = processedSite?.types?.find(x => x.id === item.type_id); return t?.converted_coin || item.sale_coin || 'BRL'; })(),
                    payment_tax: 0,
                    types: processedSite?.types || item.site?.offers?.map(o => ({
                        id: o.type_id,
                        name: o.type?.name,
                        converted_price: o.sale,
                        converted_coin: o.sale_coin,
                        cost: o.cost,
                        cost_coin: o.cost_coin,
                        seller: o.seller,
                        seller_id: o.seller_id,
                    })) || [],
                    offers: processedSite?.offers || [],
                    sellers: processedSite?.sellers || [],
                    link: '',
                    observations: '',
                    received: false,
                    paid: false,
                    comissioned: false,
                    link_status: null,
                };
            });
        }
    }
});

watch(() => form.deferred_payment, (newValue) => {
    if (newValue) {
        form.payment_date = null;
    }
});

const submit = () => {
    if (!form.client_id) {
        toast.add({
            severity: 'warn',
            summary: $t('Warning'),
            detail: $t('Please select a client'),
            life: 3000
        });
        return;
    }

    const clientExists = clientOptions.value.some(client => client.value === form.client_id);
    if (!clientExists) {
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Selected client does not exist'),
            life: 3000
        });
        return;
    }

    if (!isFormValid.value) {
        toast.add({
            severity: 'warn',
            summary: $t('Warning'),
            detail: $t('Please select a type for all sites'),
            life: 3000
        });
        return;
    }

    form.transform((data) => ({
        client_id: data.client_id,
        mode: 'order',
        notify_seller: data.notify_seller,
        notify_client: data.notify_client,
        selected_currency: currentCurrency.value,
        items: data.sites.map((item, index) => ({
            site_id: item.id,
            seller_id: item.seller_id || item.seller?.id,
            type_id: item.type_id,
            link: item.link,
            observations: item.observations || '',
            cost: item.cost,
            sale: item.sale,
            payment_tax: item.payment_tax || 0,
            cost_coin: item.cost_coin || currentCurrency.value,
            sale_coin: item.sale_coin || currentCurrency.value,
            comission_coin: item.seller?.comission_coin || currentCurrency.value,
            received: item.received,
            paid: item.paid,
            comissioned: item.comissioned,
            link_status: item.link_status,
            content: itemContents[index] ? {
                ...itemContents[index],
                auto_approve: autoApproveContent.value,
            } : null
        })),
        payment_date: data.payment_date ? formatDate(data.payment_date) : null,
        deferred_payment: data.deferred_payment,
    })).post(route('orders.store'), {
        onStart: () => showLoading($t('Creating order...')),
        onFinish: () => hideLoading(),
        onError(error) {
            toast.add({ severity: 'error', summary: $t('Error'), detail: $t('Failed to create order'), life: 3000 });
        },
        onSuccess() {
            clearDraftOrder();
            toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Order created successfully'), life: 3000 });
            window.location.href = route('orders.index');
        }
    });
};

const listForm = useForm({
    list: '',
});

const list = ref({});

const selectedAddNicho = ref(null);

const nichoTypes = computed(() => {
    return (types || []).map(t => ({ label: t.name, value: t.id }));
});

const selectBestOffer = (site, preferredTypeId = null) => {
    if (!site.offers?.length) return null;

    if (preferredTypeId) {
        const preferredOffer = site.offers.find(o => o.id === preferredTypeId);
        if (preferredOffer) return preferredOffer;
    }

    const isGambling = site.gambling === true;

    if (isGambling) {
        const nicheOffer = site.offers.find(o =>
            o.seller?.name?.toLowerCase().includes('nicho') ||
            o.seller?.name?.toLowerCase().includes('bet')
        );
        if (nicheOffer) return nicheOffer;
    }

    const linkarmeOffer = site.offers.find(o =>
        o.seller?.linkarme_seller === true ||
        o.seller?.name === 'Linkarme'
    );
    if (linkarmeOffer) return linkarmeOffer;

    return site.offers.reduce((min, o) => {
        const minPrice = min.converted_price || min.cost || 0;
        const oPrice = o.converted_price || o.cost || 0;
        return oPrice < minPrice ? o : min;
    });
};

const normalizeUrl = (url) => {
    try {
        if (!url || !url.trim()) {
            return '';
        }

        let cleanUrl = url.trim();

        cleanUrl = cleanUrl.replace(/^https?:\/\//i, '');

        cleanUrl = cleanUrl.replace(/^www\./i, '');

        cleanUrl = cleanUrl.replace(/\/+$/, '');

        const queryIndex = cleanUrl.indexOf('?');
        if (queryIndex !== -1) {
            cleanUrl = cleanUrl.substring(0, queryIndex);
        }

        return cleanUrl.toLowerCase();
    } catch (e) {
        console.error('Error normalizing URL:', e);
        return url.trim();
    }
};

const go = () => {
    if (!listForm.list || !listForm.list.trim()) {
        toast.add({ severity: 'warn', summary: $t('Warning'), detail: $t('Please enter at least one site URL'), life: 3000 });
        return;
    }

    let processedList = listForm.list
        .split('\n')
        .map(url => normalizeUrl(url))
        .filter(url => url.length > 0)
        .join('\n');

    const originalList = listForm.list;
    listForm.list = processedList;

    listForm.transform(() => ({
        list: processedList,
        currency: currentCurrency.value,
    })).post(route('orders.go'), {
        preserveScroll: true,
        preserveState: true,
        onStart: () => showLoading($t('Processing order list...')),
        onFinish: () => hideLoading(),
        onSuccess: (res) => {
            openOrderDialog.value = false;

            list.value = {};
            notFoundItems.value = [];

            if (res.props?.flash?.notFound) {
                notFoundItems.value = res.props.flash.notFound || [];
            }

            if (res.props?.flash?.sites) {
                const _sites = res.props.flash.sites;

                if (_sites && Object.keys(_sites).length > 0) {
                    const siteCounts = {};
                    const siteUrls = processedList.split('\n').filter(url => url.trim());

                    siteUrls.forEach(url => {
                        siteCounts[url] = (siteCounts[url] || 0) + 1;
                    });

                    Object.keys(_sites).forEach(url => {
                        const site = _sites[url];
                        const count = siteCounts[url] || 1;

                        for (let i = 0; i < count; i++) {
                            const siteKey = count > 1 ? `${url}_${i + 1}` : url;
                            const siteEntry = { ...site };

                            siteEntry.offer = selectBestOffer(site, selectedAddNicho.value);

                            siteEntry.originalUrl = url;
                            siteEntry.instanceNumber = i + 1;
                            siteEntry.totalInstances = count;

                            list.value[siteKey] = siteEntry;
                        }
                    });
                }

                if (Object.keys(list.value).length > 0 || notFoundItems.value.length > 0) {
                    showNotFoundSection.value = true;
                    openSitesDialog.value = true;
                } else {
                    toast.add({ severity: 'info', summary: $t('Info'), detail: $t('Site with no offers found.'), life: 3000 });
                }
            } else if (notFoundItems.value.length > 0) {
                showNotFoundSection.value = true;
                openSitesDialog.value = true;
            } else {
                toast.add({ severity: 'error', summary: $t('Error'), detail: $t('Unexpected response from server'), life: 3000 });
            }
        },
        onError: (errors) => {
            listForm.list = originalList;

            let errorMessage = $t('Failed to process site list');
            if (errors.message) {
                errorMessage = errors.message;
            } else if (errors.list) {
                errorMessage = errors.list[0] || errorMessage;
            } else if (typeof errors === 'object') {
                const firstError = Object.values(errors)[0];
                if (Array.isArray(firstError)) {
                    errorMessage = firstError[0];
                } else if (typeof firstError === 'string') {
                    errorMessage = firstError;
                }
            }

            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: errorMessage,
                life: 5000
            });
        }
    });
};

const openOrderDialog = ref(false);
const openSitesDialog = ref(false);

const notFoundItems = ref([]);
const showNotFoundSection = ref(true);
const createdSites = ref([]);

const quickOfferModalVisible = ref(false);
const quickOfferInitialUrl = ref('');
const quickOfferSiteExists = ref(false);
const quickOfferSiteId = ref(null);
const quickOfferSiteDa = ref(null);
const quickOfferSiteDr = ref(null);
const openEditDialog = ref(false);
const openLinkDialog = ref(false);
const linkValue = ref('');

const editingColumn = ref(null);
const editingRow = ref(null);
const editingSite = ref(null);
const editingValue = ref('');

const add = (site = null, index) => {
    if (site) {
        const newSite = structuredClone(toRaw(site));
        newSite.link = null;
        newSite.observations = '';
        newSite.received = false;
        newSite.paid = false;
        newSite.comissioned = false;
        newSite.link_status = null;
        newSite.types = newSite.types || [];
        newSite.sellers = newSite.sellers || [];

        if (newSite.offer) {
            newSite.cost = 0;
            newSite.cost_coin = currentCurrency.value;
            newSite.seller = newSite.offer.seller;
            newSite.seller_id = newSite.offer.seller_id;
            newSite.type_id = null;
            newSite.sale = 0;
            newSite.sale_coin = currentCurrency.value;
            newSite.payment_tax = 0;
        } else {
            newSite.cost = 0;
            newSite.sale = 0;
            newSite.cost_coin = currentCurrency.value;
            newSite.sale_coin = currentCurrency.value;
            newSite.seller_id = null;
            newSite.type_id = null;
            newSite.payment_tax = 0;
        }

        form.sites.splice(index, 0, newSite);
        toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Site duplicated successfully'), life: 2000 });
        return;
    }

    const sitesAdded = Object.keys(list.value).length;
    if (sitesAdded === 0 && createdSites.value.length === 0) {
        toast.add({ severity: 'warn', summary: $t('Warning'), detail: $t('No sites available to add'), life: 3000 });
        return;
    }

    for (let key of Object.keys(list.value)) {
        const siteData = structuredClone(toRaw(list.value[key]));

        siteData.url = siteData.originalUrl || siteData.url;

        siteData.link = null;
        siteData.observations = '';
        siteData.received = false;
        siteData.paid = false;
        siteData.comissioned = false;
        siteData.link_status = null;
        siteData.types = siteData.types || [];
        siteData.sellers = siteData.sellers || [];
        siteData.link_type = siteData.link_type || 'DOFOLLOW';
        siteData.type_observation = siteData.type_observation || '';

        if (siteData.offer) {
            siteData.seller = siteData.offer.seller;
            siteData.seller_id = siteData.offer.seller_id;
            siteData.type_id = siteData.offer.id || null;
            siteData.cost = siteData.offer.cost || 0;
            siteData.cost_coin = siteData.offer.cost_coin || currentCurrency.value;
            siteData.sale = siteData.offer.converted_price || 0;
            siteData.sale_coin = siteData.offer.converted_coin || currentCurrency.value;
            siteData.payment_tax = 0;
        } else {
            siteData.cost = 0;
            siteData.sale = 0;
            siteData.sale_coin = currentCurrency.value;
            siteData.cost_coin = currentCurrency.value;
            siteData.seller_id = null;
            siteData.type_id = null;
            siteData.payment_tax = 0;
        }

        form.sites.push(siteData);
    }

    // Add items from quick offer createdSites
    createdSites.value.forEach(created => {
        (created._items || []).forEach(item => {
            form.sites.push(item);
        });
    });

    const totalAdded = sitesAdded + createdSites.value.reduce((sum, c) => sum + c.count, 0);
    toast.add({ severity: 'success', summary: $t('Success'), detail: $t('{{count}} site(s) added to order', { count: totalAdded }), life: 3000 });
    openSitesDialog.value = false;

    list.value = {};
    listForm.list = '';
    createdSites.value = [];
};

const remove = (index) => {
    form.sites.splice(index, 1);
};

const addLink = (row) => {
    editingRow.value = row;
    linkValue.value = form.sites[editingRow.value].link;
    openLinkDialog.value = true;
};

const addLinkConfirm = () => {
    form.sites[editingRow.value].link = linkValue.value.replace(/^https?:\/\//, '');
    openLinkDialog.value = false;
    linkValue.value = '';
};

const edit = (column, row, site) => {
    editingColumn.value = column;
    editingRow.value = row;
    editingSite.value = site;

    editingCurrency.value = currentCurrency.value;

    const originalCurrency = site[`${column}_coin`] || 'USD';
    const originalValue = site[column] || 0;
    editingValue.value = convertPrice(originalValue, originalCurrency, currentCurrency.value);

    openEditDialog.value = true;
};

const editOrderPaymentTax = () => {
    if (form.sites.length === 0) {
        toast.add({ severity: 'warn', summary: $t('Warning'), detail: $t('Add sites first to edit payment tax'), life: 3000 });
        return;
    }

    editingColumn.value = 'payment_tax';
    editingRow.value = null;
    editingSite.value = null;
    editingCurrency.value = currentCurrency.value;

    let total = 0;
    for (let site of form.sites) {
        const currency = site.sale_coin || 'USD';
        const paymentTax = site.payment_tax || 0;
        total += convertPrice(paymentTax, currency, currentCurrency.value);
    }

    editingValue.value = total;

    openEditDialog.value = true;
};

const update = (all) => {
    if (!editingValue.value || editingValue.value < 0) return;

    const column = editingColumn.value;

    if (column === 'payment_tax' && editingRow.value === null) {
        const totalValue = editingValue.value;
        const numSites = form.sites.length;

        if (numSites > 0) {
            const valuePerSite = totalValue / numSites;

            form.sites.forEach(site => {
                const siteCurrency = site.sale_coin || 'USD';
                const convertedValue = convertPrice(valuePerSite * 100, currentCurrency.value, siteCurrency);
                site.payment_tax = Math.floor(convertedValue * 100);
            });
        }
    }
    else if (all) {
        form.sites
            .filter(site => site.url === editingSite.value.url)
            .forEach(site => {
                const siteCurrency = site[`${column}_coin`] || 'USD';
                const convertedValue = convertPrice(editingValue.value * 100, currentCurrency.value, siteCurrency);
                site[column] = Math.floor(convertedValue * 100);
            });
    } else {
        const site = form.sites[editingRow.value];
        const siteCurrency = site[`${column}_coin`] || 'USD';
        const convertedValue = convertPrice(editingValue.value * 100, currentCurrency.value, siteCurrency);
        site[column] = Math.floor(convertedValue * 100);
    }

    openEditDialog.value = false;
    editingValue.value = '';
};

const showEditAll = computed(() => {
    if (editingSite.value === null || editingColumn.value === 'payment_tax') {
        return false;
    }
    return form.sites.filter(site => site.url === editingSite.value.url).length > 1;
});

const formatCurrency = (value, currencyCode) => {
    const currency = currencyCode || currentCurrency.value;
    const currencyConfig = coins[currency];
    if (!currencyConfig) return value;

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};

const costTotal = computed(() => {
    let total = 0;

    for (let site of form.sites) {
        if (site.type_id && site.cost) {
            const currency = site.cost_coin || 'USD';
            total += convertPrice(site.cost, currency, currentCurrency.value);
        }
    }

    return formatWithSymbol(total, currentCurrency.value);
});

const saleTotal = computed(() => {
    let total = 0;

    for (let site of form.sites) {
        if (site.sale) {
            const currency = site.sale_coin || 'USD';
            total += convertPrice(site.sale, currency, currentCurrency.value);
        }
    }

    return formatWithSymbol(total, currentCurrency.value);
});

const paymentTaxTotal = computed(() => {
    let total = 0;

    for (let site of form.sites) {
        const currency = site.sale_coin || 'USD';
        const paymentTax = site.payment_tax || 0;
        total += convertPrice(paymentTax, currency, currentCurrency.value);
    }

    return formatWithSymbol(total, currentCurrency.value);
});

const paymentTaxPercentage = computed(() => {
    return parseFloat(payment_settings?.payment_processing_tax || 0);
});

const grandTotal = computed(() => {
    let total = 0;

    for (let site of form.sites) {
        const currency = site.sale_coin || 'USD';
        const saleValue = site.sale || 0;
        const paymentTax = site.payment_tax || 0;
        total += convertPrice(saleValue + paymentTax, currency, currentCurrency.value);
    }

    return formatWithSymbol(total, currentCurrency.value);
});

const onTypeChange = (siteIndex) => {
    const site = form.sites[siteIndex];
    if (site.type_id) {
        const selectedOffer = site.types?.find(offer => offer.id === site.type_id);

        if (selectedOffer) {
            site.cost = selectedOffer.cost || 0;
            site.cost_coin = selectedOffer.cost_coin || currentCurrency.value;
            site.sale = selectedOffer.converted_price;
            site.sale_coin = selectedOffer.converted_coin;
            site.seller_id = selectedOffer.seller_id;
            site.seller = selectedOffer.seller;

            const taxPercentage = parseFloat(payment_settings?.payment_processing_tax || 0);
            site.payment_tax = Math.floor((site.sale || 0) * (taxPercentage / 100));
        }
    } else {
        site.cost = 0;
        site.sale = 0;
        site.seller_id = null;
        site.seller = null;
        site.payment_tax = 0;
    }
};

const isFormValid = computed(() => {
    return form.sites.every(site =>
        site.type_id !== null &&
        site.type_id !== undefined
    );
});

const isPaymentConfirmed = computed(() => {
    return form.deferred_payment || (form.payment_date !== null && form.payment_date !== '');
});

const isContentComplete = computed(() => {
    return Object.keys(itemContents).length === form.sites.length;
});

const isClientApprovalReady = computed(() => {
    if (autoApproveContent.value) return true;
    const itemsWithContentLink = Object.entries(itemContents).filter(
        ([_, content]) => content && !content.skipped && (content.content_link || content.content_link_file)
    );
    if (itemsWithContentLink.length === 0) return true;
    return itemsWithContentLink.every(([_, content]) => content.send_to_client_approval);
});

const isAllStepsComplete = computed(() => {
    if (form.sites.length === 0) return false;
    if (!form.client_id) return false;
    return isFormValid.value && isPaymentConfirmed.value && isContentComplete.value && isClientApprovalReady.value;
});

const allContentSentToPortal = computed(() => {
    if (itemsWithContent.value.length === 0) return false;

    return itemsWithContent.value.every(item => {
        return item.content &&
               item.content.publication_date !== null &&
               item.content.publication_date !== undefined;
    });
});

const addSites = () => {
    openOrderDialog.value = true;
};

// ─── Suggestions ────────────────────────────────────────────────────────────
const clientHistory = ref([]);
const clientHistoryLoading = ref(false);

watch(() => form.client_id, async (newClientId) => {
    if (newClientId) {
        clientHistoryLoading.value = true;
        try {
            const res = await axios.get(route('budgets.suggestions'), {
                params: { client_id: newClientId, currency: currentCurrency.value },
            });
            clientHistory.value = res.data.history || [];
        } catch {
            clientHistory.value = [];
        } finally {
            clientHistoryLoading.value = false;
        }
    } else {
        clientHistory.value = [];
    }
});

const _pushSuggestion = (suggestion) => {
    const siteData = JSON.parse(JSON.stringify(suggestion));
    siteData.link = null;
    siteData.observations = '';
    siteData.received = false;
    siteData.paid = false;
    siteData.comissioned = false;
    siteData.link_status = null;
    siteData.types = siteData.types || [];
    siteData.sellers = siteData.sellers || [];
    siteData.link_type = siteData.link_type || 'DOFOLLOW';
    siteData.type_observation = siteData.type_observation || '';

    const bestOffer = selectBestOffer(siteData, null);
    if (bestOffer) {
        siteData.seller = bestOffer.seller;
        siteData.seller_id = bestOffer.seller_id;
        siteData.type_id = bestOffer.id || null;
        siteData.cost = bestOffer.cost || 0;
        siteData.cost_coin = bestOffer.cost_coin || currentCurrency.value;
        siteData.sale = bestOffer.converted_price || 0;
        siteData.sale_coin = bestOffer.converted_coin || currentCurrency.value;
    } else {
        siteData.cost = 0;
        siteData.sale = 0;
        siteData.sale_coin = currentCurrency.value;
        siteData.cost_coin = currentCurrency.value;
        siteData.seller_id = null;
        siteData.type_id = null;
    }
    siteData.payment_tax = 0;
    form.sites.push(siteData);
};

const addSuggestion = (suggestion) => {
    if (form.sites.some(s => s.id === suggestion.id)) {
        toast.add({ severity: 'info', summary: $t('Info'), detail: $t('Site already in order'), life: 2000 });
        return;
    }
    _pushSuggestion(suggestion);
    toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Site added to order'), life: 2000 });
};

const addAllSuggestions = (sites) => {
    if (!sites || sites.length === 0) return;
    let added = 0;
    sites.forEach(site => {
        if (!form.sites.some(s => s.id === site.id)) {
            _pushSuggestion(site);
            added++;
        }
    });
    if (added > 0) {
        toast.add({ severity: 'success', summary: $t('Success'), detail: $t('{{count}} site(s) added', { count: added }), life: 3000 });
    }
};

const linkStatusOptions = computed(() => [
    { label: $t('Enviado ao portal'), value: 'SUBMITTED' },
    { label: $t('Em redação'), value: 'PRODUCTION' },
    { label: $t('Aguardando aprovação'), value: 'WAITING' },
    { label: $t('Publicado'), value: 'PUBLISHED' }
]);

const statusOptions = computed(() => {
    return Object.entries(statuses).map(([key, label]) => ({
        label: label,
        value: key
    }));
});

const clientOptions = computed(() => {
    return clients.map(client => {
        const fullName = [client.first_name, client.last_name]
            .filter(Boolean)
            .join(' ')
            .trim();

        return {
            label: fullName || client.name,
            value: client.id,
            client: client
        };
    });
});

const filteredClients = ref([]);
const selectedClient = ref(null);

watch(() => form.client_id, (newClientId) => {
    if (newClientId && !selectedClient.value) {
        const client = clientOptions.value.find(c => c.value === newClientId);
        if (client) {
            selectedClient.value = client;
        }
    }
}, { immediate: true });

const searchClients = (event) => {
    const query = event.query.toLowerCase();
    filteredClients.value = clientOptions.value.filter(client =>
        client.label.toLowerCase().includes(query)
    );
};

const onClientSelect = (event) => {
    const client = event.value;
    if (client && client.value) {
        form.client_id = client.value;
        selectedClient.value = client;
    }
};

const onClientClear = () => {
    form.client_id = null;
    selectedClient.value = null;
};

const typeOptions = computed(() => {
    return types.map(type => ({
        label: type.name,
        value: type.id
    }));
});

const currencyOptions = computed(() => {
    return Object.keys(coins).map(currency => ({
        label: currency,
        value: currency
    }));
});

const getTypeOptions = (siteIndex) => {
    const site = form.sites[siteIndex];
    if (!site.types) return [];

    const uniqueTypes = new Map();

    site.types.forEach(type => {
        if (!uniqueTypes.has(type.id)) {
            const priceDisplay = formatConvertedPrice(
                type.converted_price,
                type.converted_coin
            );

            uniqueTypes.set(type.id, {
                label: `${type.name} - ${priceDisplay}`,
                value: type.id
            });
        }
    });

    return Array.from(uniqueTypes.values());
};

const openContentModal = (itemIndex) => {
    const item = form.sites[itemIndex];

    currentItemIndex.value = itemIndex;
    selectedItems.value = [item];

    siteFeatures.value = {
        menu: item.menu || false,
        banner: item.banner || false,
        promo: item.promo || false,
        google_news: item.google_news || false,
        embed: item.embed || false,
        restrict: item.restrict || false,
        restrict_detail: item.restrict_detail || '',
        archive_article: item.archive_article || false,
        archive_due: item.archive_due || '',
        images: item.images || false,
    };

    const selectedType = item.types?.find(t => t.id === item.type_id);
    const acceptedAnchors = selectedType?.accepted_anchors || 1;

    selectedOrderItem.value = {
        accepted_anchors: acceptedAnchors,
        url: item.url,
        type_name: selectedType?.name
    };

    showContentModal.value = true;
};

const closeContentModal = () => {
    showContentModal.value = false;
    selectedItems.value = [];
    siteFeatures.value = null;
    selectedOrderItem.value = null;
    currentItemIndex.value = null;
};

const onContentSubmit = async (contentData) => {
    if (currentItemIndex.value !== null) {
        itemContents[currentItemIndex.value] = { ...contentData };

        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('Content saved successfully'),
            life: 3000
        });

        closeContentModal();
    }
};

const maxAnchorsComputed = computed(() => {
    if (selectedItems.value.length === 0) return 1;
    return Math.max(...selectedItems.value.map(item => {
        const selectedType = item.types?.find(t => t.id === item.type_id);
        return selectedType?.accepted_anchors || 1;
    }));
});

const toggleContentItemExpansion = (itemIndex) => {
    expandedContentItems.value[itemIndex] = !expandedContentItems.value[itemIndex];
};

const openSendApprovalModal = (index) => {
    pendingApprovalIndex.value = index;
    showSendApprovalModal.value = true;
};

const closeSendApprovalModal = () => {
    showSendApprovalModal.value = false;
    pendingApprovalIndex.value = null;
};

const confirmSendApproval = () => {
    const index = pendingApprovalIndex.value;
    if (index !== null && itemContents[index]) {
        itemContents[index].send_to_client_approval = true;
    }
    closeSendApprovalModal();
};

const itemsWithContent = computed(() => {
    return form.sites.map((site, index) => {
        const hasContent = itemContents[index] && Object.keys(itemContents[index]).length > 0;
        if (!hasContent) return null;

        const selectedType = site.types?.find(t => t.id === site.type_id);

        return {
            id: index,
            site_url: site.url,
            type_name: selectedType?.name || '-',
            site: site,
            observations: site.observations || '',
            site_features: {
                accepted_anchors: selectedType?.accepted_anchors || 1,
                menu: site.menu || false,
                banner: site.banner || false,
                promo: site.promo || false,
                google_news: site.google_news || false,
                embed: site.embed || false,
                restrict: site.restrict || false,
                restrict_detail: site.restrict_detail || '',
                archive_article: site.archive_article || false,
                archive_due: site.archive_due || '',
                images: site.images || false,
            },
            content: {
                id: index,
                confirmed_by_admin_at: new Date(),
                sent_to_portal_at: (itemContents[index]?.publication_date !== null && itemContents[index]?.publication_date !== undefined) ? new Date() : null,
                ...itemContents[index]
            }
        };
    }).filter(item => item !== null);
});

const formatLinkType = (linkType) => {
    if (!linkType) return 'NOFOLLOW';

    const normalizedType = linkType.toUpperCase().trim();

    if (normalizedType.includes('DOFOLLOW')) {
        return 'DOFOLLOW';
    }

    if (normalizedType.includes('SPONSOR')) {
        return 'SPONSORED';
    }

    return 'NOFOLLOW';
};

/**
 * Get price indicator for a niche/type compared to other available niches
 * Returns: 'lowest' | 'highest' | null
 */
const getNichePriceIndicator = (types, typeId) => {
    if (!types || types.length <= 1 || !typeId) return null;

    const prices = types
        .filter(t => t.converted_price && t.converted_price > 0)
        .map(t => ({ id: t.id, price: t.converted_price }));

    if (prices.length <= 1) return null;

    const minPrice = Math.min(...prices.map(p => p.price));
    const maxPrice = Math.max(...prices.map(p => p.price));

    if (minPrice === maxPrice) return null;

    const currentType = prices.find(p => p.id === typeId);
    if (!currentType) return null;

    if (currentType.price === minPrice) return 'lowest';
    if (currentType.price === maxPrice) return 'highest';
    return null;
};

/**
 * Get price indicator for a seller compared to other sellers
 * Compares the lowest priced niche of each seller
 * Returns: 'lowest' | 'highest' | null
 */
const getSellerPriceIndicator = (types, sellerId) => {
    if (!types || !sellerId) return null;

    // Group types by seller and find lowest price for each
    const sellerLowestPrices = new Map();
    types.forEach(t => {
        if (!t.seller_id || !t.converted_price || t.converted_price <= 0) return;
        const current = sellerLowestPrices.get(t.seller_id);
        if (!current || t.converted_price < current) {
            sellerLowestPrices.set(t.seller_id, t.converted_price);
        }
    });

    if (sellerLowestPrices.size <= 1) return null;

    const prices = Array.from(sellerLowestPrices.values());
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    if (minPrice === maxPrice) return null;

    const currentSellerPrice = sellerLowestPrices.get(sellerId);
    if (!currentSellerPrice) return null;

    if (currentSellerPrice === minPrice) return 'lowest';
    if (currentSellerPrice === maxPrice) return 'highest';
    return null;
};

/**
 * Get all niches for the selected seller with price indicators
 */
const getNichesForSeller = (types, sellerId) => {
    if (!types) return [];
    const filtered = sellerId ? types.filter(t => t.seller_id === sellerId) : types;
    return filtered;
};

onCurrencyChange(({ currency }) => {
    currentCurrency.value = currency;
    // coinStore.coin = currency;
});

const openQuickOfferModal = (item) => {
    quickOfferInitialUrl.value = item.normalized_url || item.original_url || '';
    quickOfferSiteExists.value = item.reason === 'no_valid_offers';
    quickOfferSiteId.value = item.site_id || null;
    quickOfferSiteDa.value = item.da || null;
    quickOfferSiteDr.value = item.dr || null;
    quickOfferModalVisible.value = true;
};

const handleQuickOfferCreated = (data) => {
    const createdUrl = data.site?.url;
    if (createdUrl) {
        notFoundItems.value = notFoundItems.value.filter(
            item => item.normalized_url !== createdUrl && item.site_url !== createdUrl
        );
    }

    if (data.site && data.typesPayload && data.typesPayload.length > 0) {
        const addedNiches = [];
        const items = [];

        data.typesPayload.forEach(typePayload => {
            const typeForDropdown = {
                id: typePayload.type_id,
                name: typePayload.type_name,
                converted_price: typePayload.sale,
                converted_coin: typePayload.sale_coin,
                cost: typePayload.cost,
                cost_coin: typePayload.cost_coin,
                seller_id: data.seller?.id || null,
                seller: data.seller || null,
            };

            items.push({
                id: data.site.id,
                url: data.site.url,
                da: data.site.da || 0,
                dr: data.site.dr || 0,
                link_type: data.site.link_type || 'DOFOLLOW',
                seller_id: data.seller?.id || null,
                seller: data.seller || null,
                type_id: typePayload.type_id,
                cost: typePayload.cost,
                cost_coin: typePayload.cost_coin,
                sale: typePayload.sale,
                sale_coin: typePayload.sale_coin,
                payment_tax: 0,
                link: '',
                observations: '',
                types: [typeForDropdown],
                sellers: [data.seller],
            });

            addedNiches.push(typePayload.type_name);
        });

        createdSites.value.push({
            url: data.site.url,
            da: data.site.da || 0,
            dr: data.site.dr || 0,
            seller: data.seller?.name || '-',
            niches: addedNiches,
            count: data.typesPayload.length,
            _items: items,
        });

        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('Offer registered. Click "Add to order" to include it.'),
            life: 3000,
        });
    }
};

const removeNotFoundItem = (index) => {
    notFoundItems.value.splice(index, 1);
};
</script>

<template>

    <!-- Add Sites Dialog -->
    <Dialog v-model:visible="openOrderDialog" :modal="true" :style="{ width: '50vw' }" @hide="selectedAddNicho = null">
        <template #header>
            <div class="flex items-center justify-between w-full pr-2">
                <span class="font-bold text-xl">{{ $t('Add sites') }}</span>
                <Select
                    v-model="selectedAddNicho"
                    :options="nichoTypes"
                    option-label="label"
                    option-value="value"
                    :placeholder="$t('Select niche')"
                    class="w-48"
                    show-clear
                />
            </div>
        </template>
        <div class="field">
            <FloatLabel>
                <Textarea
                    id="list"
                    v-model="listForm.list"
                    rows="5"
                    class="w-full"
                    :placeholder="$t('Enter site URLs, one per line. Example:\nsite1.com\nsite2.com\nsite1.com')"
                />
                <label for="list">{{ $t('Site list') }}</label>
            </FloatLabel>
            <small v-if="listForm.errors.list" class="p-error">{{ listForm.errors.list }}</small>
        </div>

        <template #footer>
            <Button :label="$t('Cancel')" icon="pi pi-times" text @click="openOrderDialog = false" />
            <Button :label="$t('Go')" icon="pi pi-check" @click="go" :disabled="listForm.processing" />
        </template>
    </Dialog>

    <!-- Sites Selection Dialog -->
    <Dialog v-model:visible="openSitesDialog" :header="$t('Add sites')" :modal="true" :style="{ width: '80vw', maxHeight: '90vh' }" :contentStyle="{ overflow: 'auto' }" @hide="notFoundItems = []; createdSites = []">

        <!-- Not Found Items Section (Red) -->
        <div v-if="notFoundItems.length > 0" class="mb-4">
            <div class="bg-red-50 border border-red-200 rounded-lg overflow-hidden">
                <div
                    class="flex items-center justify-between p-3 bg-red-100 border-b border-red-200 cursor-pointer"
                    @click="showNotFoundSection = !showNotFoundSection"
                >
                    <div class="flex items-center gap-2">
                        <i class="pi pi-exclamation-circle text-red-600"></i>
                        <span class="font-semibold text-red-700">
                            {{ $t('Sites Not Found') }} ({{ notFoundItems.length }})
                        </span>
                    </div>
                    <i :class="showNotFoundSection ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="text-red-600"></i>
                </div>

                <div v-if="showNotFoundSection" class="p-3">
                    <DataTable :value="notFoundItems" responsiveLayout="scroll" class="p-datatable-sm">
                        <Column :header="$t('URL')" style="min-width: 200px">
                            <template #body="slotProps">
                                <span class="text-sm">{{ slotProps.data.original_url || slotProps.data.normalized_url }}</span>
                            </template>
                        </Column>
                        <Column :header="$t('Reason')" style="min-width: 150px">
                            <template #body="slotProps">
                                <Tag
                                    :value="slotProps.data.reason === 'site_not_found' ? $t('Site not found') : $t('No offers')"
                                    :severity="slotProps.data.reason === 'site_not_found' ? 'danger' : 'warn'"
                                />
                            </template>
                        </Column>
                        <Column :header="$t('Actions')" style="min-width: 200px">
                            <template #body="slotProps">
                                <div class="flex gap-2">
                                    <Button
                                        :label="$t('Quick Offer')"
                                        icon="pi pi-plus"
                                        size="small"
                                        severity="success"
                                        @click="openQuickOfferModal(slotProps.data)"
                                    />
                                    <Button
                                        icon="pi pi-trash"
                                        size="small"
                                        severity="danger"
                                        text
                                        @click="removeNotFoundItem(slotProps.index)"
                                    />
                                </div>
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </div>
        </div>

        <!-- Sites Created & Added Section (Blue) -->
        <div v-if="createdSites.length > 0" class="mb-4">
            <div class="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
                <div class="flex items-center justify-between p-3 bg-blue-100 border-b border-blue-200">
                    <div class="flex items-center gap-2">
                        <i class="pi pi-check-circle text-blue-600"></i>
                        <span class="font-semibold text-blue-700">
                            {{ $t('Sites Created & Added') }} ({{ createdSites.length }})
                        </span>
                    </div>
                </div>
                <div class="p-3">
                    <DataTable :value="createdSites" responsiveLayout="scroll" class="p-datatable-sm">
                        <Column :header="$t('URL')" style="min-width: 200px">
                            <template #body="slotProps">
                                <span class="text-sm font-medium">{{ slotProps.data.url }}</span>
                            </template>
                        </Column>
                        <Column header="DA" style="min-width: 60px">
                            <template #body="slotProps">
                                <span class="text-sm">{{ slotProps.data.da }}</span>
                            </template>
                        </Column>
                        <Column header="DR" style="min-width: 60px">
                            <template #body="slotProps">
                                <span class="text-sm">{{ slotProps.data.dr }}</span>
                            </template>
                        </Column>
                        <Column :header="$t('Seller')" style="min-width: 120px">
                            <template #body="slotProps">
                                <span class="text-sm">{{ slotProps.data.seller }}</span>
                            </template>
                        </Column>
                        <Column :header="$t('Niches')" style="min-width: 150px">
                            <template #body="slotProps">
                                <div class="flex flex-wrap gap-1">
                                    <Tag
                                        v-for="niche in slotProps.data.niches"
                                        :key="niche"
                                        :value="niche"
                                        severity="info"
                                        class="text-xs"
                                    />
                                </div>
                            </template>
                        </Column>
                        <Column :header="$t('Items')" style="min-width: 80px">
                            <template #body="slotProps">
                                <Tag :value="slotProps.data.count + ' ' + $t('items')" severity="success" />
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </div>
        </div>

        <!-- Valid Sites Table -->
        <DataTable
            v-if="Object.keys(list).length > 0"
            :value="Object.entries(list).map(([key, site]) => ({key, url: site.originalUrl || site.url, ...site}))"
            responsiveLayout="scroll"
            class="p-datatable-sm"
        >
            <Column :header="$t('Portal')">
                <template #body="slotProps">
                    <div class="flex items-center gap-2">
                        <span>{{ slotProps.data.originalUrl || slotProps.data.url }}</span>
                        <span v-if="slotProps.data.totalInstances > 1" class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            {{ slotProps.data.instanceNumber }}/{{ slotProps.data.totalInstances }}
                        </span>
                    </div>
                </template>
            </Column>
            <Column field="offer.seller.name" :header="$t('Seller')">
                <template #body="slotProps">
                    <span>{{ slotProps.data.offer?.seller?.name ?? '-' }}</span>
                </template>
            </Column>
            <Column :header="$t('Purchase Value')">
                <template #body="slotProps">
                    <span v-if="slotProps.data.offer === null">-</span>
                    <span v-else>
                        {{ formatConvertedPrice(slotProps.data.offer.cost, slotProps.data.offer.cost_coin) }}
                    </span>
                </template>
            </Column>
            <Column :header="$t('Sale value')">
                <template #body="slotProps">
                    <span v-if="slotProps.data.offer === null">-</span>
                    <span v-else>
                        <div v-if="slotProps.data.offer.calculated_price && slotProps.data.offer.calculated_price > 0">
                            {{ formatConvertedPrice(slotProps.data.offer.calculated_price, slotProps.data.offer.cost_coin) }}
                        </div>
                        <div v-else-if="slotProps.data.offer.sale && slotProps.data.offer.sale > 0">
                            {{ formatConvertedPrice(slotProps.data.offer.sale, slotProps.data.offer.sale_coin) }}
                        </div>
                        <div v-else>-</div>
                    </span>
                </template>
            </Column>
        </DataTable>

        <template #footer>
            <Button
                :label="$t('Edit List')"
                icon="pi pi-pencil"
                severity="secondary"
                text
                @click="openSitesDialog = false; openOrderDialog = true"
            />
            <Button :label="$t('Cancel')" icon="pi pi-times" text @click="openSitesDialog = false" />
            <Button
                v-if="Object.keys(list).length > 0 || createdSites.length > 0"
                :label="$t('Add to order')"
                icon="pi pi-check"
                @click="add(null)"
            />
        </template>
    </Dialog>

    <!-- Quick Offer Modal -->
    <QuickOfferModal
        v-model:visible="quickOfferModalVisible"
        :initial-url="quickOfferInitialUrl"
        :site-exists="quickOfferSiteExists"
        :site-id="quickOfferSiteId"
        :site-da="quickOfferSiteDa"
        :site-dr="quickOfferSiteDr"
        :currency="currentCurrency"
        context="order"
        @created="handleQuickOfferCreated"
    />

    <!-- Edit Value Dialog -->
    <Dialog v-model:visible="openEditDialog" :modal="true" :style="{ width: '35vw' }">
        <template #header>
            <span v-if="editingColumn === 'cost'">{{ $t('Edit purchase value') }}</span>
            <span v-if="editingColumn === 'sale'">{{ $t('Edit sale value') }}</span>
            <span v-if="editingColumn === 'payment_tax' && editingRow === null">{{ $t('Edit order payment tax') }}</span>
        </template>

        <div class="field">
            <label for="value" class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Value') }}</label>
            <InputNumber
                id="value"
                v-model="editingValue"
                :currency="editingCurrency"
                :min="0"
                :step="0.01"
                :minFractionDigits="2"
                :maxFractionDigits="2"
                mode="currency"
                class="w-full"
            />
        </div>

        <template #footer>
            <Button :label="$t('Back')" icon="pi pi-times" text @click="openEditDialog = false" />
            <Button :label="$t('Update')" icon="pi pi-check" @click="update(false)" />
            <Button v-if="showEditAll" :label="$t('Update all')" icon="pi pi-check-circle" @click="update(true)" severity="success" />
        </template>
    </Dialog>

    <!-- Add Link Dialog -->
    <Dialog v-model:visible="openLinkDialog" :header="$t('Add link')" :modal="true" :style="{ width: '30vw' }">
        <div class="field">
            <FloatLabel>
                <InputText
                    id="link"
                    v-model="linkValue"
                    class="w-full"
                />
                <label for="link">{{ $t('Link') }}</label>
            </FloatLabel>
        </div>

        <template #footer>
            <Button :label="$t('Back')" icon="pi pi-times" text @click="openLinkDialog = false" />
            <Button :label="$t('Add')" icon="pi pi-check" @click="addLinkConfirm" />
        </template>
    </Dialog>

    <!-- Main Form -->
    <div class="grid">
        <div class="col-12">
            <Card>
                <template #title>
                    <div class="flex align-items-center gap-2 mb-6">
                        <i class="pi pi-plus-circle text-2xl"></i>
                        <span class="flex items-center">{{ $t('Add order') }}</span>
                    </div>
                </template>

                <template #content>
                    <form @submit.prevent="submit">
                        <div class="grid gap-10">
                            <!-- Client (required for orders, optional for budgets) -->
                            <div class="col-12 md:col-6">
                                <div class="field">
                                    <label class="block text-sm font-medium text-surface-700 mb-2">
                                        {{ $t('Client') }} <span class="text-red-500">*</span>
                                    </label>
                                    <AutoComplete
                                        v-model="selectedClient"
                                        :suggestions="filteredClients"
                                        @complete="searchClients"
                                        @item-select="onClientSelect"
                                        @clear="onClientClear"
                                        optionLabel="label"
                                        :placeholder="$t('Search for a client...')"
                                        class="w-full"
                                        :class="{ 'p-invalid': !form.client_id && form.sites.length > 0 }"
                                        :minLength="1"
                                        :dropdown="true"
                                        :forceSelection="false"
                                    />
                                    <small v-if="form.errors.client_id" class="p-error">{{ form.errors.client_id }}</small>
                                </div>
                            </div>

                            <!-- Sites Table and Summary -->
                            <div class="col-12">
                                <div class="field">
                                    <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
                                        <!-- Sites Table -->
                                        <div class="lg:col-span-3">
                                            <Card>
                                                <template #title>
                                                    <div class="flex items-center justify-between">
                                                        <span>{{ $t('Order Sites') }}</span>
                                                        <span class="text-sm font-normal text-surface-600">{{ form.sites?.length || 0 }} {{ (form.sites?.length || 0) === 1 ? $t('site') : $t('sites') }}</span>
                                                    </div>
                                                </template>
                                                <template #content>
                                                    <DataTable
                                                        :value="form.sites"
                                                        responsiveLayout="scroll"
                                                        class="p-datatable-sm"
                                                        :scrollable="true"
                                                        scrollHeight="400px"
                                                    >
                                                        <template #empty>
                                                            <div class="text-center p-4">
                                                                {{ $t('No site have been added to this order yet.') }}
                                                            </div>
                                                        </template>

                                                        <Column :header="$t('Actions')" style="width: 100px">
                                                            <template #body="slotProps">
                                                                <div class="flex gap-2">
                                                                    <Button
                                                                        icon="pi pi-trash"
                                                                        severity="danger"
                                                                        text
                                                                        rounded
                                                                        size="small"
                                                                        @click="remove(slotProps.index)"
                                                                    />
                                                                    <Button
                                                                        icon="pi pi-copy"
                                                                        severity="info"
                                                                        text
                                                                        rounded
                                                                        size="small"
                                                                        @click="add(slotProps.data, slotProps.index)"
                                                                    />
                                                                </div>
                                                            </template>
                                                        </Column>

                                                        <Column field="url" :header="$t('Portal')" style="min-width: 160px; max-width: 220px;">
                                                            <template #body="slotProps">
                                                                <div class="flex items-center gap-2 min-w-0">
                                                                    <span class="text-sm truncate" :title="slotProps.data.url">{{ slotProps.data.url }}</span>
                                                                    <a :href="route('sites.edit', slotProps.data.id)" target="_blank" class="text-blue-500 flex-shrink-0">
                                                                        <i class="pi pi-external-link text-xs"></i>
                                                                    </a>
                                                                </div>
                                                            </template>
                                                        </Column>

                                                        <Column :header="$t('Seller')" style="min-width: 180px">
                                                            <template #body="slotProps">
                                                                <Select
                                                                    v-if="slotProps.data.types && slotProps.data.types.length > 1"
                                                                    v-model="slotProps.data.seller_id"
                                                                    :options="(() => {
                                                                        const unique = new Map();
                                                                        (slotProps.data.types || []).forEach(t => {
                                                                            if (t.seller_id && !unique.has(t.seller_id)) {
                                                                                unique.set(t.seller_id, { label: t.seller?.name || 'Seller #' + t.seller_id, value: t.seller_id });
                                                                            }
                                                                        });
                                                                        return Array.from(unique.values());
                                                                    })()"
                                                                    optionLabel="label"
                                                                    optionValue="value"
                                                                    :placeholder="$t('Select seller')"
                                                                    class="w-full"
                                                                    @change="(() => {
                                                                        const site = form.sites[slotProps.index];
                                                                        const selectedType = site.types?.find(t => t.seller_id === site.seller_id);
                                                                        if (selectedType) {
                                                                            site.seller = selectedType.seller;
                                                                            site.type_id = selectedType.id;
                                                                            onTypeChange(slotProps.index);
                                                                        }
                                                                    })()"
                                                                >
                                                                    <template #option="optionProps">
                                                                        <div class="flex items-center gap-1 w-full">
                                                                            <span>{{ optionProps.option.label }}</span>
                                                                            <i
                                                                                v-if="getSellerPriceIndicator(slotProps.data.types, optionProps.option.value) === 'lowest'"
                                                                                class="pi pi-arrow-down text-green-600"
                                                                                style="font-size: 0.65rem"
                                                                                v-tooltip.top="$t('Lowest price among sellers')"
                                                                            ></i>
                                                                            <i
                                                                                v-else-if="getSellerPriceIndicator(slotProps.data.types, optionProps.option.value) === 'highest'"
                                                                                class="pi pi-arrow-up text-red-600"
                                                                                style="font-size: 0.65rem"
                                                                                v-tooltip.top="$t('Highest price among sellers')"
                                                                            ></i>
                                                                        </div>
                                                                    </template>
                                                                    <template #value="valueProps">
                                                                        <div v-if="valueProps.value" class="flex items-center gap-1">
                                                                            <span>{{ (() => {
                                                                                const opt = slotProps.data.types?.find(t => t.seller_id === valueProps.value);
                                                                                return opt?.seller?.name || 'Seller #' + valueProps.value;
                                                                            })() }}</span>
                                                                            <i
                                                                                v-if="getSellerPriceIndicator(slotProps.data.types, valueProps.value) === 'lowest'"
                                                                                class="pi pi-arrow-down text-green-600"
                                                                                style="font-size: 0.65rem"
                                                                                v-tooltip.top="$t('Lowest price among sellers')"
                                                                            ></i>
                                                                            <i
                                                                                v-else-if="getSellerPriceIndicator(slotProps.data.types, valueProps.value) === 'highest'"
                                                                                class="pi pi-arrow-up text-red-600"
                                                                                style="font-size: 0.65rem"
                                                                                v-tooltip.top="$t('Highest price among sellers')"
                                                                            ></i>
                                                                        </div>
                                                                        <span v-else>{{ $t('Select seller') }}</span>
                                                                    </template>
                                                                </Select>
                                                                <span v-else class="text-sm text-surface-700">
                                                                    {{ slotProps.data.seller?.name || '-' }}
                                                                </span>
                                                            </template>
                                                        </Column>

                                                        <Column :header="$t('Type')" style="min-width: 200px">
                                                            <template #body="slotProps">
                                                                <Select
                                                                    v-model="slotProps.data.type_id"
                                                                    :options="getNichesForSeller(slotProps.data.types, slotProps.data.seller_id)"
                                                                    optionLabel="name"
                                                                    optionValue="id"
                                                                    :placeholder="$t('Choose a type')"
                                                                    class="w-full"
                                                                    @change="onTypeChange(slotProps.index)"
                                                                >
                                                                    <template #option="optionProps">
                                                                        <div class="flex items-center justify-between w-full">
                                                                            <div class="flex items-center gap-1">
                                                                                <span>{{ optionProps.option.name }}</span>
                                                                                <i
                                                                                    v-if="getNichePriceIndicator(getNichesForSeller(slotProps.data.types, slotProps.data.seller_id), optionProps.option.id) === 'lowest'"
                                                                                    class="pi pi-arrow-down text-green-600"
                                                                                    style="font-size: 0.65rem"
                                                                                    v-tooltip.top="$t('Lowest price among niches')"
                                                                                ></i>
                                                                                <i
                                                                                    v-else-if="getNichePriceIndicator(getNichesForSeller(slotProps.data.types, slotProps.data.seller_id), optionProps.option.id) === 'highest'"
                                                                                    class="pi pi-arrow-up text-red-600"
                                                                                    style="font-size: 0.65rem"
                                                                                    v-tooltip.top="$t('Highest price among niches')"
                                                                                ></i>
                                                                            </div>
                                                                            <span class="font-semibold text-primary-600 ml-2">
                                                                                {{ formatConvertedPrice(optionProps.option.converted_price, optionProps.option.converted_coin) }}
                                                                            </span>
                                                                        </div>
                                                                    </template>
                                                                    <template #value="valueProps">
                                                                        <div v-if="valueProps.value" class="flex items-center gap-1">
                                                                            <span>{{ (() => {
                                                                                const opt = slotProps.data.types?.find(t => t.id === valueProps.value);
                                                                                return opt?.name || '';
                                                                            })() }}</span>
                                                                            <i
                                                                                v-if="getNichePriceIndicator(getNichesForSeller(slotProps.data.types, slotProps.data.seller_id), valueProps.value) === 'lowest'"
                                                                                class="pi pi-arrow-down text-green-600"
                                                                                style="font-size: 0.65rem"
                                                                                v-tooltip.top="$t('Lowest price among niches')"
                                                                            ></i>
                                                                            <i
                                                                                v-else-if="getNichePriceIndicator(getNichesForSeller(slotProps.data.types, slotProps.data.seller_id), valueProps.value) === 'highest'"
                                                                                class="pi pi-arrow-up text-red-600"
                                                                                style="font-size: 0.65rem"
                                                                                v-tooltip.top="$t('Highest price among niches')"
                                                                            ></i>
                                                                        </div>
                                                                        <span v-else>{{ $t('Choose a type') }}</span>
                                                                    </template>
                                                                </Select>
                                                                <small v-if="!slotProps.data.type_id" class="text-red-500">{{ $t('Required') }}</small>
                                                            </template>
                                                        </Column>

                                                        <Column :header="$t('Link Type')" style="min-width: 120px">
                                                            <template #body="slotProps">
                                                                <div class="relative inline-flex items-center justify-center">
                                                                    <div class="flex items-center gap-1 px-3 py-2 bg-surface-50 border border-surface-200 rounded-md text-surface-900 text-center">
                                                                        {{ formatLinkType(slotProps.data.link_type) }}
                                                                        <i
                                                                            v-if="slotProps.data.type_observation"
                                                                            class="pi pi-info-circle text-blue-500 cursor-pointer"
                                                                            v-tooltip.top="slotProps.data.type_observation"
                                                                            style="font-size: 0.7rem;"
                                                                        ></i>
                                                                    </div>
                                                                </div>
                                                            </template>
                                                        </Column>

                                                        <Column :header="$t('Observations')" style="min-width: 250px">
                                                            <template #body="slotProps">
                                                                <Textarea
                                                                    v-model="slotProps.data.observations"
                                                                    rows="1"
                                                                    autoResize
                                                                    class="w-full"
                                                                    :placeholder="$t('Add notes...')"
                                                                />
                                                            </template>
                                                        </Column>

                                                        <Column :header="$t('Purchase Value')">
                                                            <template #body="slotProps">
                                                                <span v-if="slotProps.data.cost" class="whitespace-nowrap">
                                                                    {{ formatConvertedPrice(slotProps.data.cost, slotProps.data.cost_coin || 'USD') }}
                                                                </span>
                                                                <span v-else>-</span>
                                                            </template>
                                                        </Column>

                                                        <Column :header="$t('Sale Value')">
                                                            <template #body="slotProps">
                                                                <div v-if="slotProps.data.sale" class="flex items-center gap-1 whitespace-nowrap">
                                                                    <span>
                                                                        {{ formatConvertedPrice(slotProps.data.sale, slotProps.data.sale_coin || 'USD') }}
                                                                    </span>
                                                                    <Button
                                                                        icon="pi pi-pencil"
                                                                        text
                                                                        rounded
                                                                        size="small"
                                                                        class="ml-1"
                                                                        @click="edit('sale', slotProps.index, slotProps.data)"
                                                                    />
                                                                </div>
                                                                <span v-else>-</span>
                                                            </template>
                                                        </Column>
                                                    </DataTable>

                                                    <Button
                                                        :label="$t('Add sites')"
                                                        icon="pi pi-plus"
                                                        class="mt-4"
                                                        @click="addSites"
                                                    />

                                                    <BudgetSuggestionsPanel
                                                        :best-sellers="bestSellers"
                                                        :client-history="clientHistory"
                                                        :client-history-loading="clientHistoryLoading"
                                                        :recommended="recommended"
                                                        :client-selected="!!form.client_id"
                                                        :existing-site-ids="form.sites.map(s => s.id).filter(Boolean)"
                                                        @add="addSuggestion"
                                                        @add-all="addAllSuggestions"
                                                    />
                                                </template>
                                            </Card>
                                        </div>

                                        <!-- Order Summary -->
                                        <div class="lg:col-span-1">
                                            <Card class="sticky top-4">
                                                <template #title>
                                                    {{ $t('Order Summary') }}
                                                </template>
                                                <template #content>
                                                    <div class="space-y-4">
                                                        <div class="flex justify-between items-center">
                                                            <span class="text-surface-700">{{ $t('Total Sites') }}:</span>
                                                            <span class="font-semibold text-lg">{{ form.sites.length }}</span>
                                                        </div>

                                        <Divider />

                                        <div class="flex justify-between items-center">
                                            <span class="text-surface-700">{{ $t('Purchase Value') }}:</span>
                                            <span class="font-semibold whitespace-nowrap">{{ costTotal || '--' }}</span>
                                        </div>

                                        <div class="flex justify-between items-center">
                                            <span class="text-surface-700">{{ $t('Sale Value') }}:</span>
                                            <span class="font-semibold text-primary-600 whitespace-nowrap"> {{ saleTotal }}</span>
                                        </div>                                                        <div class="flex justify-between items-center cursor-pointer" @click="editOrderPaymentTax">
                                                            <span class="text-surface-700">
                                                                {{ $t('Payment Processing Tax') }} ({{ paymentTaxPercentage }}%):
                                                            </span>
                                                            <div class="flex items-center gap-1">
                                                                <span class="font-semibold text-orange-600 whitespace-nowrap"> {{ paymentTaxTotal }}</span>
                                                                <Button
                                                                    icon="pi pi-pencil"
                                                                    text
                                                                    rounded
                                                                    size="small"
                                                                    class="ml-1"
                                                                    @click="editOrderPaymentTax"
                                                                />
                                                            </div>
                                                        </div>

                                                        <Divider />

                                                        <div class="flex justify-between items-center">
                                                            <span class="text-xl font-semibold text-surface-900">{{ $t('Total') }}:</span>
                                                            <span class="text-xl font-bold text-primary-600 whitespace-nowrap"> {{ grandTotal }}</span>
                                                        </div>
                                                    </div>
                                                </template>
                                            </Card>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Payment Confirmation Section -->
                            <div class="col-12">
                                <Card class="mb-6">
                                    <template #header>
                                        <div class="p-6 pb-0">
                                            <div class="flex items-center gap-3">
                                                <div class="w-10 h-10 rounded-full flex items-center justify-center"
                                                    :class="isPaymentConfirmed ? 'bg-green-100' : 'bg-amber-100'">
                                                    <span class="font-bold text-lg"
                                                        :class="isPaymentConfirmed ? 'text-green-700' : 'text-amber-700'">1</span>
                                                </div>
                                                <div>
                                                    <h2 class="text-xl font-bold text-surface-900">
                                                        {{ $t('Payment Confirmation') }}
                                                    </h2>
                                                    <p class="text-sm font-medium"
                                                        :class="isPaymentConfirmed ? 'text-green-600' : 'text-amber-600'">
                                                        {{ isPaymentConfirmed ? $t('Payment confirmed') : $t('Configure payment settings for this order') }}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <template #content>
                                        <div class="space-y-4">
                                            <!-- Payment Method Selection -->
                                            <div class="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                                <div class="flex items-start gap-3">
                                                    <Checkbox
                                                        v-model="form.deferred_payment"
                                                        inputId="deferred_payment"
                                                        :binary="true"
                                                    />
                                                    <label for="deferred_payment" class="cursor-pointer">
                                                        <div class="font-medium text-surface-900">{{ $t('Deferred Payment') }}</div>
                                                        <div class="text-sm text-surface-600">{{ $t('When this option is selected, payment will be processed at the end of the purchase.') }}</div>
                                                    </label>
                                                </div>
                                            </div>

                                            <!-- Payment Date Input -->
                                            <div v-if="!form.deferred_payment" class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                                                <label class="block text-sm font-medium text-surface-700 mb-3">
                                                    {{ $t('Select the Payment Date') }}
                                                    <span class="text-red-500">*</span>
                                                </label>
                                                <Calendar
                                                    v-model="form.payment_date"
                                                    showIcon
                                                    dateFormat="dd/mm/yy"
                                                    class="w-full md:w-[400px]"
                                                    :class="{ 'p-invalid': form.errors.payment_date }"
                                                />
                                                <small v-if="form.errors.payment_date" class="text-red-600 block mt-2">
                                                    {{ form.errors.payment_date }}
                                                </small>
                                            </div>
                                        </div>
                                    </template>
                                </Card>
                            </div>

                            <!-- Content Management Section -->
                            <div class="col-12" v-if="form.sites.length > 0">
                                <Card class="mb-6">
                                    <template #header>
                                        <div class="p-6 pb-0">
                                            <div class="flex items-center gap-3">
                                                <div class="w-10 h-10 rounded-full flex items-center justify-center"
                                                    :class="Object.keys(itemContents).length === form.sites.length ? 'bg-green-100' : 'bg-amber-100'">
                                                    <span class="font-bold text-lg"
                                                        :class="Object.keys(itemContents).length === form.sites.length ? 'text-green-700' : 'text-amber-700'">2</span>
                                                </div>
                                                <div class="flex-1">
                                                    <h2 class="text-xl font-bold text-surface-900">
                                                        {{ $t('Content Management') }}
                                                    </h2>
                                                    <p class="text-sm font-medium"
                                                        :class="Object.keys(itemContents).length === form.sites.length ? 'text-green-600' : 'text-amber-600'">
                                                        <span v-if="Object.keys(itemContents).length === 0">{{ $t('No content added yet') }}</span>
                                                        <span v-else-if="Object.keys(itemContents).length === form.sites.length">
                                                            {{ isClientApprovalReady ? $t('Approval requested') : $t('Pending client approval request') }}
                                                        </span>
                                                        <span v-else>{{ $t('Content progress') }} ({{ Object.keys(itemContents).length }}/{{ form.sites.length }})</span>
                                                    </p>
                                                </div>
                                                <div v-if="Object.keys(itemContents).length > 0" class="flex items-center gap-2 ml-auto p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                                    <i class="pi pi-shield text-amber-500"></i>
                                                    <label for="auto-approve-content" class="text-sm font-medium text-amber-800 cursor-pointer select-none">
                                                        {{ $t('Auto Approve (skip client review)') }}
                                                    </label>
                                                    <Checkbox
                                                        v-model="autoApproveContent"
                                                        inputId="auto-approve-content"
                                                        :binary="true"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <template #content>
                                        <div class="space-y-4">
                                            <!-- Content Items -->
                                            <Card v-for="(site, index) in form.sites" :key="index" class="shadow-sm">
                                                <template #header>
                                                    <div class="p-4 pb-0">
                                                        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                                                            <div class="flex items-center gap-3">
                                                                <div class="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                                                                    <span class="font-bold text-primary-700">{{ index + 1 }}</span>
                                                                </div>
                                                                <div>
                                                                    <h3 class="font-semibold text-surface-900">
                                                                        {{ site.url }}
                                                                    </h3>
                                                                    <p class="text-sm text-surface-600">
                                                                        {{ site.types?.find(t => t.id === site.type_id)?.name || $t('No type selected') }}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div class="flex items-center gap-3">
                                                                <Tag v-if="itemContents[index] && Object.keys(itemContents[index]).length > 0"
                                                                     :value="$t('Content Added')" severity="success" icon="pi pi-check" />
                                                                <Tag v-else :value="$t('Pending Content')" severity="warn" icon="pi pi-clock" />
                                                                <Tag
                                                                    v-if="itemContents[index]?.skipped"
                                                                    :value="$t('Sem Validação')"
                                                                    severity="warn"
                                                                    icon="pi pi-exclamation-triangle"
                                                                    v-tooltip.top="$t('Conteúdo salvo sem validação de âncoras')"
                                                                />

                                                                <Button
                                                                    :label="itemContents[index] ? $t('Edit Content') : $t('Add Content')"
                                                                    :icon="itemContents[index] ? 'pi pi-pencil' : 'pi pi-plus'"
                                                                    :severity="itemContents[index] ? 'secondary' : 'primary'"
                                                                    :disabled="!site.type_id"
                                                                    size="small"
                                                                    @click="openContentModal(index)"
                                                                />

                                                                <Button
                                                                    v-if="itemContents[index] && Object.keys(itemContents[index]).length > 0"
                                                                    :icon="expandedContentItems[index] ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                                                                    :label="$t('Show')"
                                                                    size="small"
                                                                    severity="secondary"
                                                                    outlined
                                                                    @click="toggleContentItemExpansion(index)"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                                <template #content>
                                                    <div v-show="expandedContentItems[index]" v-if="itemContents[index] && Object.keys(itemContents[index]).length > 0">
                                                        <div class="space-y-4">
                                                            <!-- Site Information Section -->
                                                            <SiteFeaturesInfo :features="site" :accepted-anchors="site.types?.find(t => t.id === site.type_id)?.accepted_anchors || 1" />

                                                            <!-- Anchors Section -->
                                                            <div v-if="itemContents[index].anchors && itemContents[index].anchors.length > 0"
                                                                 class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                                                <h5 class="text-sm font-semibold text-blue-900 mb-3">
                                                                    <i class="pi pi-link text-blue-700 mr-2"></i>
                                                                    {{ $t('Anchors') }}
                                                                </h5>

                                                                <div class="flex flex-col md:flex-row gap-4">
                                                                    <!-- Dynamic Anchors -->
                                                                    <div v-for="(anchor, aIndex) in itemContents[index].anchors.slice(0, 3)" :key="aIndex" class="flex-1">
                                                                        <div class="space-y-2">
                                                                            <div>
                                                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                                                    {{ $t('Anchor Name') }} {{ aIndex + 1 }}
                                                                                </label>
                                                                                <p class="text-sm text-blue-900">{{ anchor.text || '-' }}</p>
                                                                            </div>
                                                                            <div>
                                                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                                                    {{ $t('Anchor URL') }} {{ aIndex + 1 }}
                                                                                </label>
                                                                                <p class="text-xs text-blue-900 break-all">{{ anchor.url || '-' }}</p>
                                                                            </div>
                                                                        </div>

                                                                        <!-- Divider between anchors -->
                                                                        <Divider v-if="aIndex < Math.min(itemContents[index].anchors.length - 1, 2)" layout="vertical" class="hidden md:flex" />
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <!-- Language and Article Title -->
                                                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                <div v-if="itemContents[index].language">
                                                                    <label class="block text-sm font-medium text-surface-700 mb-1">
                                                                        {{ $t('Language') }}
                                                                    </label>
                                                                    <p class="text-surface-900">{{ itemContents[index].language }}</p>
                                                                </div>

                                                                <div v-if="itemContents[index].article_title">
                                                                    <label class="block text-sm font-medium text-surface-700 mb-1">
                                                                        {{ $t('Article Title') }}
                                                                    </label>
                                                                    <p class="text-surface-900">{{ itemContents[index].article_title }}</p>
                                                                </div>
                                                            </div>

                                                            <!-- Content Link or File -->
                                                            <div v-if="itemContents[index].content_link || itemContents[index].content_link_file" class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                                                                <label class="block text-sm font-medium text-surface-700 mb-2">
                                                                    {{ $t('Content') }}
                                                                </label>

                                                                <div v-if="itemContents[index].content_link" class="flex items-center gap-2">
                                                                    <i class="pi pi-link text-primary-500"></i>
                                                                    <a
                                                                        :href="itemContents[index].content_link"
                                                                        target="_blank"
                                                                        class="text-primary-500 hover:text-primary-600 underline break-all"
                                                                    >
                                                                        {{ itemContents[index].content_link }}
                                                                    </a>
                                                                </div>

                                                                <div v-if="itemContents[index].content_link_file" class="flex items-center gap-2">
                                                                    <i class="pi pi-file text-primary-500"></i>
                                                                    <span class="text-surface-900">{{ $t('File attached') }}</span>
                                                                </div>

                                                                <!-- Send to Client Approval button -->
                                                                <div class="flex justify-end mt-3">
                                                                    <Button
                                                                        v-if="!itemContents[index].send_to_client_approval"
                                                                        :label="$t('Send to Client Approval')"
                                                                        icon="pi pi-send"
                                                                        severity="info"
                                                                        size="small"
                                                                        :disabled="autoApproveContent"
                                                                        @click="openSendApprovalModal(index)"
                                                                    />
                                                                    <div v-else class="flex items-center gap-2 text-green-700">
                                                                        <i class="pi pi-check-circle"></i>
                                                                        <span class="text-sm font-medium">{{ $t('Request will be sent on save') }}</span>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <!-- Observations -->
                                                            <div v-if="itemContents[index].observations || site.observations" class="p-4 bg-amber-50 rounded-lg border border-amber-200">
                                                                <label class="block text-sm font-medium text-amber-900 mb-2">
                                                                    <i class="pi pi-info-circle mr-1"></i>
                                                                    {{ $t('Observations') }}
                                                                </label>
                                                                <p v-if="itemContents[index].observations" class="text-surface-900 mb-2">{{ itemContents[index].observations }}</p>
                                                                <p v-if="site.observations" class="text-surface-900">{{ $t('Site') }}: {{ site.observations }}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                            </Card>

                                            <small v-if="form.sites.some(site => !site.type_id)" class="text-orange-600">
                                                <i class="pi pi-exclamation-triangle mr-1"></i>
                                                {{ $t('Select a type for all sites to enable content management') }}
                                            </small>
                                        </div>
                                    </template>
                                </Card>
                            </div>

                            <!-- Notification Settings -->
                            <div class="col-12" v-if="form.sites.length > 0">
                                <Card class="mb-6">
                                    <template #header>
                                        <div class="p-6 pb-0">
                                            <div class="flex items-center gap-3">
                                                <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                    <i class="pi pi-bell text-blue-700 text-lg"></i>
                                                </div>
                                                <div>
                                                    <h2 class="text-xl font-bold text-surface-900">
                                                        {{ $t('Notification Settings') }}
                                                    </h2>
                                                    <p class="text-sm font-medium text-blue-600">
                                                        {{ $t('Configure who will be notified about order progress') }}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <template #content>
                                        <div class="space-y-4">
                                            <div class="flex items-start gap-3">
                                                <Checkbox
                                                    v-model="form.notify_seller"
                                                    inputId="notify_seller"
                                                    :binary="true"
                                                />
                                                <label for="notify_seller" class="cursor-pointer">
                                                    <div class="font-medium text-surface-900">{{ $t('Notify seller about new order') }}</div>
                                                    <div class="text-sm text-surface-600">{{ $t('Seller will receive an email about this new order') }}</div>
                                                </label>
                                            </div>

                                            <div class="flex items-start gap-3">
                                                <Checkbox
                                                    v-model="form.notify_client"
                                                    inputId="notify_client"
                                                    :binary="true"
                                                />
                                                <label for="notify_client" class="cursor-pointer">
                                                    <div class="font-medium text-surface-900">{{ $t('Notify client about new order') }}</div>
                                                    <div class="text-sm text-surface-600">{{ $t('Client will receive an email about this new order') }}</div>
                                                </label>
                                            </div>
                                        </div>
                                    </template>
                                </Card>
                            </div>

                            <!-- Submit Button -->
                            <div class="col-12">
                                <div class="flex gap-3 flex-wrap">
                                    <Button
                                        type="submit"
                                        :label="$t('Save')"
                                        icon="pi pi-save"
                                        :loading="form.processing"
                                        :disabled="form.processing || !isAllStepsComplete"
                                    />
                                </div>

                                <div v-if="form.sites.length > 0" class="text-sm mt-2">
                                    <div v-if="!form.client_id" class="text-orange-600">
                                        <i class="pi pi-exclamation-triangle mr-1"></i>
                                        {{ $t('Please select a client to continue') }}
                                    </div>
                                    <div v-else-if="!isFormValid" class="text-orange-600">
                                        <i class="pi pi-exclamation-triangle mr-1"></i>
                                        {{ $t('Please select a type for all sites to continue') }}
                                    </div>
                                    <div v-else-if="!isPaymentConfirmed" class="text-orange-600">
                                        <i class="pi pi-exclamation-triangle mr-1"></i>
                                        {{ $t('Please configure payment settings to continue') }}
                                    </div>
                                    <div v-else-if="!isContentComplete" class="text-orange-600">
                                        <i class="pi pi-exclamation-triangle mr-1"></i>
                                        {{ $t('Please add content for all sites to continue') }}
                                    </div>
                                    <div v-else-if="!isClientApprovalReady && !autoApproveContent" class="text-orange-600">
                                        <i class="pi pi-exclamation-triangle mr-1"></i>
                                        {{ $t('Please send content to client approval before saving') }}
                                    </div>
                                    <div v-else-if="isAllStepsComplete" class="text-green-600">
                                        <i class="pi pi-check-circle mr-1"></i>
                                        {{ $t('Ready to create order!') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </template>
            </Card>
        </div>
    </div>

    <SendForApprovalModal
        v-model:visible="showSendApprovalModal"
        @confirm="confirmSendApproval"
    />

    <!-- Content Modal -->
    <ContentModal
        v-model:visible="showContentModal"
        :header="currentItemIndex !== null ? (itemContents[currentItemIndex] ? $t('Edit Content') : $t('Add Content')) : $t('Add Content')"
        :site-features="siteFeatures"
        :max-anchors="maxAnchorsComputed"
        :selected-order-item="selectedOrderItem"
        :languages="formattedLanguages"
        :submitting="submittingContent"
        :initial-data="currentItemIndex !== null ? (itemContents[currentItemIndex] || {}) : {}"
        :hide-auto-content="true"
        :allow-skip="true"
        @submit="onContentSubmit"
        @close="closeContentModal"
    />
</template>

<style scoped>
.truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.currency-selector :deep(.p-select) {
    height: 41px;
}
</style>
